// n8n-chat/src/components/ui/reset-password.tsx
"use client";

import * as React from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { Eye, EyeOff } from "lucide-react";
import { z } from "zod";

import { authClient } from "@/lib/auth-client";
import {
  AuthLayout,
  GlassInputWrapper,
  type Testimonial,
} from "@/components/auth/auth-layout";

const requestSchema = z.object({
  email: z.string().trim().email("Adresse email invalide."),
});

const resetSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, "Minimum 8 caractères.")
      .max(128, "Mot de passe trop long."),
    confirmPassword: z.string().min(1, "Confirmation requise."),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    path: ["confirmPassword"],
    message: "Les mots de passe ne correspondent pas.",
  });

type RequestValues = z.infer<typeof requestSchema>;
type ResetValues = z.infer<typeof resetSchema>;
type FieldErrors<T> = Partial<Record<keyof T, string>>;

export function ResetPasswordPage({
  title = "Réinitialiser le mot de passe",
  description = "Recevez un lien de réinitialisation par email.",
  heroImageSrc,
  testimonials = [],
}: {
  readonly title?: string;
  readonly description?: string;
  readonly heroImageSrc?: string;
  readonly testimonials?: Testimonial[];
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const token = searchParams.get("token");

  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);

  const [requestValues, setRequestValues] = React.useState<RequestValues>({
    email: "",
  });
  const [requestErrors, setRequestErrors] = React.useState<
    FieldErrors<RequestValues>
  >({});
  const [requestStatus, setRequestStatus] = React.useState<
    "idle" | "success" | "error"
  >("idle");

  const [resetValues, setResetValues] = React.useState<ResetValues>({
    newPassword: "",
    confirmPassword: "",
  });
  const [resetErrors, setResetErrors] = React.useState<
    FieldErrors<ResetValues>
  >({});
  const [resetStatus, setResetStatus] = React.useState<
    "idle" | "success" | "error"
  >("idle");

  const [formError, setFormError] = React.useState<string | null>(null);
  const [pending, startTransition] = React.useTransition();

  const isResetMode = Boolean(token);

  function validateRequest(payload: RequestValues): payload is RequestValues {
    const parsed = requestSchema.safeParse(payload);
    if (parsed.success) return true;

    const next: FieldErrors<RequestValues> = {};
    for (const issue of parsed.error.issues) {
      const k = issue.path[0];
      if (k === "email") next.email = issue.message;
    }
    setRequestErrors(next);
    return false;
  }

  function validateReset(payload: ResetValues): payload is ResetValues {
    const parsed = resetSchema.safeParse(payload);
    if (parsed.success) return true;

    const next: FieldErrors<ResetValues> = {};
    for (const issue of parsed.error.issues) {
      const k = issue.path[0];
      if (k === "newPassword") next.newPassword = issue.message;
      if (k === "confirmPassword") next.confirmPassword = issue.message;
    }
    setResetErrors(next);
    return false;
  }

  function onSubmitRequest(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);
    setRequestStatus("idle");

    const payload: RequestValues = { email: requestValues.email.trim() };
    if (!validateRequest(payload)) return;

    startTransition(async () => {
      const redirectTo = `${globalThis.location.origin}/reset-password`;

      const { error } = await authClient.requestPasswordReset({
        email: payload.email,
        redirectTo,
      });

      if (error) {
        setFormError(error.message || "Une erreur est survenue.");
        setRequestStatus("error");
        return;
      }

      setRequestStatus("success");
      setRequestValues({ email: "" });
    });
  }

  function onSubmitReset(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);
    setResetStatus("idle");

    if (!token) {
      setFormError("Token manquant.");
      return;
    }

    const payload: ResetValues = {
      newPassword: resetValues.newPassword,
      confirmPassword: resetValues.confirmPassword,
    };

    if (!validateReset(payload)) return;

    startTransition(async () => {
      const { error } = await authClient.resetPassword({
        token,
        newPassword: payload.newPassword,
      });

      if (error) {
        setFormError(error.message || "Une erreur est survenue.");
        setResetStatus("error");
        return;
      }

      setResetStatus("success");
      router.push("/sign-in");
      router.refresh();
    });
  }

  return (
    <AuthLayout heroImageSrc={heroImageSrc} testimonials={testimonials}>
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="mb-1 font-display text-3xl font-semibold leading-tight text-white">
            {title}
          </h1>
          <p className="mb-4 font-sans text-sm text-white/60">{description}</p>
        </div>

        {formError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-3 py-2 font-sans text-xs text-destructive">
            {formError}
          </div>
        ) : null}

        {!isResetMode ? (
          <>
            <form className="space-y-4" onSubmit={onSubmitRequest}>
              <div>
                <label className="mb-1.5 block font-display text-xs font-medium text-white/80">
                  Email
                </label>
                <GlassInputWrapper>
                  <input
                    name="email"
                    type="email"
                    value={requestValues.email}
                    onChange={(e) => {
                      setRequestValues({ email: e.target.value });
                      setRequestErrors((p) => ({ ...p, email: undefined }));
                      setFormError(null);
                    }}
                    placeholder="votre@email.com"
                    autoComplete="email"
                    className="w-full rounded-2xl bg-transparent p-3 font-sans text-sm text-white placeholder:text-white/30 focus:outline-none"
                  />
                </GlassInputWrapper>
                {requestErrors.email ? (
                  <p className="mt-1 text-xs text-destructive">
                    {requestErrors.email}
                  </p>
                ) : null}
              </div>

              <button
                type="submit"
                disabled={pending}
                className="w-full rounded-xl bg-[#6729FF] py-3 font-display text-sm font-semibold text-white shadow-[0_0_20px_rgba(103,41,255,0.3)] transition-all duration-300 hover:bg-[#5220cc] hover:shadow-[0_0_30px_rgba(103,41,255,0.5)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {pending ? "Envoi..." : "Envoyer le lien"}
              </button>
            </form>

            {requestStatus === "success" ? (
              <p className="font-sans text-xs text-emerald-400">
                Si un compte existe, un email a été envoyé.
              </p>
            ) : null}

            <p className="text-center font-sans text-xs text-white/60">
              <Link
                href="/sign-in"
                className="font-sans font-medium text-[#6729FF] transition-colors hover:underline"
              >
                Retour à la connexion
              </Link>
            </p>
          </>
        ) : (
          <>
            <form className="space-y-4" onSubmit={onSubmitReset}>
              <div>
                <label className="mb-1.5 block font-display text-xs font-medium text-white/80">
                  Nouveau mot de passe
                </label>
                <GlassInputWrapper>
                  <div className="relative">
                    <input
                      name="newPassword"
                      type={showPassword ? "text" : "password"}
                      value={resetValues.newPassword}
                      onChange={(e) => {
                        setResetValues((p) => ({
                          ...p,
                          newPassword: e.target.value,
                        }));
                        setResetErrors((p) => ({
                          ...p,
                          newPassword: undefined,
                        }));
                        setFormError(null);
                      }}
                      placeholder="Votre nouveau mot de passe"
                      autoComplete="new-password"
                      className="w-full rounded-2xl bg-transparent p-3 pr-10 font-sans text-sm text-white placeholder:text-white/30 focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute inset-y-0 right-3 flex items-center"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-white/40 transition-colors hover:text-white" />
                      ) : (
                        <Eye className="h-4 w-4 text-white/40 transition-colors hover:text-white" />
                      )}
                    </button>
                  </div>
                </GlassInputWrapper>
                {resetErrors.newPassword ? (
                  <p className="mt-1 text-xs text-destructive">
                    {resetErrors.newPassword}
                  </p>
                ) : null}
              </div>

              <div>
                <label className="mb-1.5 block font-display text-xs font-medium text-white/80">
                  Confirmer le mot de passe
                </label>
                <GlassInputWrapper>
                  <div className="relative">
                    <input
                      name="confirmPassword"
                      type={showConfirm ? "text" : "password"}
                      value={resetValues.confirmPassword}
                      onChange={(e) => {
                        setResetValues((p) => ({
                          ...p,
                          confirmPassword: e.target.value,
                        }));
                        setResetErrors((p) => ({
                          ...p,
                          confirmPassword: undefined,
                        }));
                        setFormError(null);
                      }}
                      placeholder="Confirmez le mot de passe"
                      autoComplete="new-password"
                      className="w-full rounded-2xl bg-transparent p-3 pr-10 font-sans text-sm text-white placeholder:text-white/30 focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((v) => !v)}
                      className="absolute inset-y-0 right-3 flex items-center"
                      aria-label={
                        showConfirm ? "Hide password" : "Show password"
                      }
                    >
                      {showConfirm ? (
                        <EyeOff className="h-4 w-4 text-white/40 transition-colors hover:text-white" />
                      ) : (
                        <Eye className="h-4 w-4 text-white/40 transition-colors hover:text-white" />
                      )}
                    </button>
                  </div>
                </GlassInputWrapper>
                {resetErrors.confirmPassword ? (
                  <p className="mt-1 text-xs text-destructive">
                    {resetErrors.confirmPassword}
                  </p>
                ) : null}
              </div>

              <button
                type="submit"
                disabled={pending}
                className="w-full rounded-xl bg-[#6729FF] py-3 font-display text-sm font-semibold text-white shadow-[0_0_20px_rgba(103,41,255,0.3)] transition-all duration-300 hover:bg-[#5220cc] hover:shadow-[0_0_30px_rgba(103,41,255,0.5)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {pending ? "Validation..." : "Réinitialiser le mot de passe"}
              </button>
            </form>

            {resetStatus === "success" ? (
              <p className="font-sans text-xs text-emerald-400">
                Mot de passe modifié.
              </p>
            ) : null}
          </>
        )}
      </div>
    </AuthLayout>
  );
}
